import React, { useCallback, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import type { ReactScrollIntoViewIfNeededProps } from 'react-scroll-into-view-if-needed';
import { xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { row_nativeIssueTable$key } from '@atlassian/jira-relay/src/__generated__/row_nativeIssueTable.graphql';
import { BORDER_WIDTH, ROW_HEIGHT } from '../../common/constants';
import { useIsSelected, useIsFocused } from '../../controllers/selected-row-state';
import BlankIssueRow from './blank-row';
import { IssueRow } from './issue-row';
import { StyledScrollIntoViewIfNeeded } from './styled';
import type { RowProps } from './types';
import { useDraggableTableRow } from './use-draggable-table-row';
import { scrollToMiddle } from './utils';

const scrollOptionsOld: ReactScrollIntoViewIfNeededProps['options'] = {
	behavior: scrollToMiddle,
	block: 'nearest',
	inline: 'nearest',
};

const Row = ({
	rowIndex,
	fragment,
	columns,
	tableInjectedProps,
	hasInlineEditing,
	hasFixedTableLayout,
	enableRowDragging,
	issuesConnectionId,
}: RowProps) => {
	const [isSelected, { setSelectedRow }] = useIsSelected({ selectedRowIndex: rowIndex });
	const [isFocused, { resetFocus }] = useIsFocused({ selectedRowIndex: rowIndex });
	const tableRowRef = useRef<HTMLTableRowElement>(null);

	const issueRowData = useFragment<row_nativeIssueTable$key>(
		graphql`
			fragment row_nativeIssueTable on JiraIssue
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				...issueRow_nativeIssueTable_IssueRowWithFragments_issue
					@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
			}
		`,
		fragment,
	);
	const { dropIndicator } = useDraggableTableRow({
		ref: tableRowRef,
		enableRowDragging,
		issueId: issueRowData?.id,
		issuesConnectionId,
	});

	useEffect(() => {
		// @ts-expect-error - We are using instance properties on the underlying class component which are not exposed as a valid ref type. We should remove our dependency on this ref as it's not typesafe.
		const tableRowElement = tableRowRef.current?.node?.current;
		if (isFocused && tableRowElement) {
			if (!tableRowElement.querySelector(':focus')) {
				tableRowElement.focus();
			}
		}
	}, [isFocused]);

	useEffect(() => {
		// Explicitly reset our internal state to track which row is focused. We do this to prevent the DOM element being re-focused unintentionally when the component is re-rendered.
		if (isFocused) {
			resetFocus();
		}
	}, [isFocused, resetFocus]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const focusCurrentRow = useCallback(() => setSelectedRow(rowIndex), [rowIndex, setSelectedRow]);

	const onClick = useCallback(() => {
		const clickAnalytics = createAnalyticsEvent({});
		const actionSubjectId = issueRowData ? 'issueRow' : 'blankRow';
		fireUIAnalytics(clickAnalytics, 'rowItem clicked', actionSubjectId);
	}, [createAnalyticsEvent, issueRowData]);

	const cellBorder = ff('jsv-one-issue-table-spreadsheet-ui_ygrnf')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useMemo(() => [cellBorderStyles, rowIndex === 0 && firstRowCellBorderStyles], [rowIndex])
		: undefined;

	const rowContent = issueRowData ? (
		<IssueRow
			issue={issueRowData}
			columns={columns}
			tableInjectedProps={tableInjectedProps}
			hasInlineEditing={hasInlineEditing}
			hasFixedTableLayout={hasFixedTableLayout}
			cellBorder={cellBorder}
		/>
	) : (
		<BlankIssueRow
			numColumns={columns.length}
			isSelected={isSelected}
			hasFixedTableLayout={hasFixedTableLayout}
			cellBorder={cellBorder}
		/>
	);

	if (ff('jsv-one-issue-table-spreadsheet-ui_ygrnf')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useLayoutEffect(() => {
			// Scroll the table row into view (if needed - block: nearest) when it becomes selected
			if (isSelected) {
				tableRowRef.current?.scrollIntoView({ block: 'nearest' });
			}
		}, [isSelected]);

		return (
			<StyledTableRow
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				data-testid="native-issue-table.ui.issue-row"
				aria-selected={isSelected}
				role="row"
				active={isSelected}
				onFocus={focusCurrentRow}
				onClick={onClick}
				tabIndex={-1}
				ref={tableRowRef}
			>
				{rowContent}
				{dropIndicator}
			</StyledTableRow>
		);
	}

	return (
		<StyledScrollIntoViewIfNeeded
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			data-testid="native-issue-table.ui.issue-row"
			elementType="tr"
			aria-selected={isSelected}
			role="row"
			active={isSelected}
			onFocus={focusCurrentRow}
			onClick={onClick}
			options={scrollOptionsOld}
			tabIndex={-1}
			ref={tableRowRef}
		>
			{rowContent}
			{dropIndicator}
		</StyledScrollIntoViewIfNeeded>
	);
};

export default Row;

const cellBorderStyles = xcss({
	borderColor: 'color.border',
	borderStyle: 'solid',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderTopWidth: BORDER_WIDTH,
	borderBottomWidth: '0',
	borderLeftWidth: '0',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRightWidth: BORDER_WIDTH,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':last-of-type': {
		borderRightWidth: '0',
	},
});

const firstRowCellBorderStyles = xcss({
	borderTopWidth: '0',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledTableRow = styled.tr<{ active: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${ROW_HEIGHT}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	position: () => (ff('jsw.enable-nin-rank-dnd') ? 'relative' : 'static'),
	'&:focus': {
		outline: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	boxShadow: ({ active }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		active ? `inset 2px 0 ${token('color.background.neutral.bold', colors.DN90A)}` : '',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ active }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		active ? token('color.background.selected', colors.B50) : token('elevation.surface', colors.N0),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: ({ active }) =>
			active
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					token('color.background.selected', colors.B50)
				: token('elevation.surface.hovered', '#F1F2F4'),
	},
});
